import { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Cpu, Activity, Filter, Search, Thermometer, Droplets, Wind, User, Calendar, TrendingUp, Wifi, HardDrive, Clock, CheckSquare, Square, RotateCcw, Zap, X, GitCompare, Settings, LayoutGrid, Network, Trophy, Shield, Camera, FlaskConical, AlertTriangle } from 'lucide-react'
import { useDeviceStatus } from '../context/DeviceStatusContext'
import { useAdminData } from '../context/AdminDataContext'
import { adminApi } from '../services/ApiService'
import Pagination from '../components/Pagination'
import './AdminPages.css'

const ITEMS_PER_PAGE = 12

export default function DeviceMonitor() {
  const navigate = useNavigate()
  const { wsDevices: activeDevices, wsConnected, sensorAlerts } = useDeviceStatus()
  const { devices: contextDevices, loading: contextLoading } = useAdminData()
  const allDevices = contextDevices
  const loading = contextLoading
  const [filter, setFilter] = useState('active')
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)

  // ── Bulk operations ──
  const [selectedDevices, setSelectedDevices] = useState(new Set())
  const [bulkMode, setBulkMode] = useState(false)
  const [bulkLoading, setBulkLoading] = useState(false)
  const [bulkResult, setBulkResult] = useState(null)
  const [compareMode, setCompareMode] = useState(false)
  const [viewMode, setViewMode] = useState('grid') // 'grid' | 'map' | 'fleet'

  // ── Bulk config modal ──
  const [configModalOpen, setConfigModalOpen] = useState(false)
  const [bulkConfigInterval, setBulkConfigInterval] = useState('')
  const [bulkConfigLoading, setBulkConfigLoading] = useState(false)

  // ── Fleet Provisioning ──
  const [fleetThings, setFleetThings] = useState([])
  const [fleetLoading, setFleetLoading] = useState(false)
  const [fleetSearch, setFleetSearch] = useState('')

  // ── ATH (All-Time High) active devices ──
  const [ath, setAth] = useState({ value: 0, reachedAt: null })

  useEffect(() => {
    adminApi.get('/admin/metrics/ath').then(data => {
      if (data?.value) setAth(data)
    }).catch(() => {})
  }, [])

  // Load fleet things when fleet view is selected
  useEffect(() => {
    if (viewMode !== 'fleet') return
    setFleetLoading(true)
    adminApi.get('/admin/fleet/things').then(data => {
      setFleetThings(data.things || [])
    }).catch(() => {}).finally(() => setFleetLoading(false))
  }, [viewMode])

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [filter, searchTerm])

  // Only include WS devices confirmed online (exclude lifecycle disconnects)
  const activeMACsSet = useMemo(
    () => new Set(
      (activeDevices || [])
        .filter(d => d.status !== 'offline' && d.online !== false)
        .map(d => d.mac?.toUpperCase())
    ),
    [activeDevices]
  )

  // Track MACs that WS explicitly says are offline (overrides stale REST isOnline)
  const offlineMACsSet = useMemo(
    () => new Set(
      (activeDevices || [])
        .filter(d => d.status === 'offline' || d.online === false)
        .map(d => d.mac?.toUpperCase())
    ),
    [activeDevices]
  )

  const filteredDevices = useMemo(() => {
    let filtered = allDevices.map(device => {
      const mac = (device.MAC || device.mac || '').toUpperCase()
      return {
      ...device,
      isActive: !offlineMACsSet.has(mac) && (device.isOnline || activeMACsSet.has(mac)),
      liveData: device.liveData || (activeDevices || []).find(d => d.mac?.toUpperCase() === mac)
    }})

    if (filter === 'active') {
      filtered = filtered.filter(device => device.isActive)
    } else if (filter === 'inactive') {
      filtered = filtered.filter(device => !device.isActive)
    } else if (filter === 'unassigned') {
      filtered = filtered.filter(device => device.isUnassigned)
    }

    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter(device => 
        device.name?.toLowerCase().includes(search) ||
        device.MAC?.toLowerCase().includes(search) ||
        device.model?.toLowerCase().includes(search) ||
        device.authUser?.name?.toLowerCase().includes(search) ||
        device.growRoom?.name?.toLowerCase().includes(search)
      )
    }

    return filtered
  }, [allDevices, filter, searchTerm, activeMACsSet, offlineMACsSet, activeDevices])

  const stats = useMemo(() => {
    const active = allDevices.filter(d => {
      const mac = (d.MAC || '').toUpperCase()
      if (offlineMACsSet.has(mac)) return false
      return activeMACsSet.has(mac)
    }).length
    const unassigned = allDevices.filter(d => d.isUnassigned).length
    return {
      total: allDevices.length,
      active,
      inactive: allDevices.length - active,
      unassigned
    }
  }, [allDevices, activeMACsSet, offlineMACsSet])

  // ── ATH update ──
  const updateATH = useCallback((currentActive) => {
    if (currentActive > ath.value) {
      adminApi.post('/admin/metrics/ath', { current: currentActive }).then(data => {
        if (data?.updated) setAth({ value: data.value, reachedAt: data.reachedAt })
      }).catch(() => {})
    }
  }, [ath.value])

  useEffect(() => {
    if (stats.active > 0) updateATH(stats.active)
  }, [stats.active, updateATH])

  // ── Map view: devices grouped by client ──
  const devicesByClient = useMemo(() => {
    const groups = {}
    for (const dev of allDevices) {
      const clientName = dev.authUser?.name || dev.authUser?.email || 'Sin asignar'
      const clientId = dev.accountId || 'unassigned'
      if (!groups[clientId]) groups[clientId] = { name: clientName, email: dev.authUser?.email || '', devices: [] }
      const mac = (dev.MAC || dev.mac || '').toUpperCase()
      groups[clientId].devices.push({
        ...dev,
        isActive: !offlineMACsSet.has(mac) && (dev.isOnline || activeMACsSet.has(mac)),
        liveData: dev.liveData || (activeDevices || []).find(d => d.mac?.toUpperCase() === mac)
      })
    }
    return Object.entries(groups).sort((a, b) => b[1].devices.length - a[1].devices.length)
  }, [allDevices, activeMACsSet, offlineMACsSet, activeDevices])

  // ── Bulk operations ──
  const toggleDevice = (mac) => {
    setSelectedDevices(prev => {
      const next = new Set(prev)
      next.has(mac) ? next.delete(mac) : next.add(mac)
      return next
    })
  }
  const selectAll = () => {
    const allMacs = paginatedDevices.map(d => d.MAC).filter(Boolean)
    setSelectedDevices(new Set(allMacs))
  }
  const deselectAll = () => setSelectedDevices(new Set())
  const handleBulkAction = async (action) => {
    if (selectedDevices.size === 0) return
    const confirmed = window.confirm(`¿${action === 'restart' ? 'Reiniciar' : 'Solicitar estado de'} ${selectedDevices.size} dispositivos?`)
    if (!confirmed) return
    setBulkLoading(true); setBulkResult(null)
    try {
      const data = await adminApi.post('/admin/commands/bulk', {
        action,
        macs: Array.from(selectedDevices)
      })
      setBulkResult(data)
      setTimeout(() => setBulkResult(null), 8000)
    } catch (e) {
      setBulkResult({ success: false, error: e.message })
    } finally { setBulkLoading(false) }
  }

  const handleBulkConfig = async () => {
    if (selectedDevices.size === 0 || !bulkConfigInterval) return
    const interval = parseInt(bulkConfigInterval)
    if (!interval || interval < 1 || interval > 3600) return
    setBulkConfigLoading(true)
    try {
      await Promise.all(
        Array.from(selectedDevices).map(mac =>
          adminApi.post('/admin/commands/config', { mac, config: { telemetry_interval: interval } })
        )
      )
      setBulkResult({ success: true, sent: selectedDevices.size })
      setTimeout(() => setBulkResult(null), 6000)
      setConfigModalOpen(false)
      setBulkConfigInterval('')
    } catch (e) {
      setBulkResult({ success: false, error: e.message })
    } finally { setBulkConfigLoading(false) }
  }

  // Pagination
  const totalPages = Math.ceil(filteredDevices.length / ITEMS_PER_PAGE)
  const paginatedDevices = filteredDevices.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  )

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('es-ES', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    })
  }

  const formatUptime = (seconds) => {
    if (!seconds && seconds !== 0) return 'N/A'
    const days = Math.floor(seconds / 86400)
    const hours = Math.floor((seconds % 86400) / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    if (days > 0) return `${days}d ${hours}h`
    if (hours > 0) return `${hours}h ${mins}m`
    return `${mins}m`
  }

  const getRssiLevel = (rssi) => {
    if (rssi >= -50) return { label: 'Excelente', color: '#10b981', bars: 4 }
    if (rssi >= -60) return { label: 'Buena', color: '#22c55e', bars: 3 }
    if (rssi >= -70) return { label: 'Regular', color: '#f59e0b', bars: 2 }
    return { label: 'Débil', color: '#ef4444', bars: 1 }
  }

  const getHeapColor = (heap) => {
    if (heap > 50000) return '#10b981'
    if (heap > 20000) return '#f59e0b'
    return '#ef4444'
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <p className="loading-text">Cargando dispositivos...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="admin-page-container">
      <div className="admin-page-content">
        {/* Header */}
        <div className="page-header">
          <div>
            <div className="page-title">
              <div className="page-title-icon">
                <Cpu className="w-7 h-7 text-white" />
              </div>
              <h1>Dispositivos</h1>
            </div>
            <p className="page-subtitle">
              Monitoreo y gestión de todos los dispositivos IoT
            </p>
          </div>
          <button 
            onClick={() => { setBulkMode(!bulkMode); deselectAll() }}
            className={`filter-btn ${bulkMode ? 'active' : ''}`}
            style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }}
          >
            <CheckSquare className="w-4 h-4" />
            {bulkMode ? 'Cancelar selección' : 'Operaciones masivas'}
          </button>

          <div style={{ display: 'flex', gap: '0.25rem', background: 'rgba(15,23,42,0.5)', borderRadius: '0.5rem', padding: '0.25rem' }}>
            <button onClick={() => setViewMode('grid')} className={`filter-btn ${viewMode === 'grid' ? 'active' : ''}`} style={{ padding: '0.4rem 0.75rem', fontSize: '0.8rem' }} title="Vista tarjetas">
              <LayoutGrid size={16} />
            </button>
            <button onClick={() => setViewMode('map')} className={`filter-btn ${viewMode === 'map' ? 'active' : ''}`} style={{ padding: '0.4rem 0.75rem', fontSize: '0.8rem' }} title="Vista mapa">
              <Network size={16} />
            </button>
            <button onClick={() => setViewMode('fleet')} className={`filter-btn ${viewMode === 'fleet' ? 'active' : ''}`} style={{ padding: '0.4rem 0.75rem', fontSize: '0.8rem' }} title="Fleet Provisioning">
              <Shield size={16} />
            </button>
          </div>

        </div>

        {/* Bulk action bar */}
        {bulkMode && (
          <div style={{
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.1) 100%)',
            border: '1px solid rgba(99, 102, 241, 0.3)',
            borderRadius: '0.75rem',
            padding: '0.75rem 1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '0.75rem',
            marginBottom: '1rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span style={{ color: '#c4b5fd', fontWeight: 600, fontSize: '0.875rem' }}>
                {selectedDevices.size} seleccionado{selectedDevices.size !== 1 ? 's' : ''}
              </span>
              <button onClick={selectAll} style={{ color: '#818cf8', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', textDecoration: 'underline' }}>
                Seleccionar página
              </button>
              {selectedDevices.size > 0 && (
                <button onClick={deselectAll} style={{ color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', textDecoration: 'underline' }}>
                  Limpiar
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => handleBulkAction('restart')}
                disabled={selectedDevices.size === 0 || bulkLoading}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.375rem',
                  padding: '0.5rem 1rem', borderRadius: '0.5rem',
                  background: selectedDevices.size > 0 ? 'rgba(239, 68, 68, 0.2)' : 'rgba(100,100,100,0.2)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  color: selectedDevices.size > 0 ? '#fca5a5' : '#6b7280',
                  cursor: selectedDevices.size > 0 ? 'pointer' : 'not-allowed',
                  fontSize: '0.8rem', fontWeight: 600
                }}
              >
                <RotateCcw size={14} /> Reiniciar
              </button>
              <button
                onClick={() => handleBulkAction('status')}
                disabled={selectedDevices.size === 0 || bulkLoading}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.375rem',
                  padding: '0.5rem 1rem', borderRadius: '0.5rem',
                  background: selectedDevices.size > 0 ? 'rgba(16, 185, 129, 0.2)' : 'rgba(100,100,100,0.2)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  color: selectedDevices.size > 0 ? '#6ee7b7' : '#6b7280',
                  cursor: selectedDevices.size > 0 ? 'pointer' : 'not-allowed',
                  fontSize: '0.8rem', fontWeight: 600
                }}
              >
                <Zap size={14} /> Solicitar Estado
              </button>
              {selectedDevices.size >= 2 && selectedDevices.size <= 4 && (
                <button
                  onClick={() => setCompareMode(true)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.375rem',
                    padding: '0.5rem 1rem', borderRadius: '0.5rem',
                    background: 'rgba(99, 102, 241, 0.2)',
                    border: '1px solid rgba(99, 102, 241, 0.3)',
                    color: '#a5b4fc',
                    cursor: 'pointer',
                    fontSize: '0.8rem', fontWeight: 600
                  }}
                >
                  <GitCompare size={14} /> Comparar ({selectedDevices.size})
                </button>
              )}
              <button
                onClick={() => setConfigModalOpen(true)}
                disabled={selectedDevices.size === 0 || bulkLoading}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.375rem',
                  padding: '0.5rem 1rem', borderRadius: '0.5rem',
                  background: selectedDevices.size > 0 ? 'rgba(251,191,36,0.15)' : 'rgba(100,100,100,0.2)',
                  border: '1px solid rgba(251,191,36,0.3)',
                  color: selectedDevices.size > 0 ? '#fde68a' : '#6b7280',
                  cursor: selectedDevices.size > 0 ? 'pointer' : 'not-allowed',
                  fontSize: '0.8rem', fontWeight: 600
                }}
              >
                <Settings size={14} /> Configurar
              </button>
            </div>
          </div>
        )}

        {/* Bulk result notification */}
        {bulkResult && (
          <div style={{
            background: bulkResult.success ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
            border: `1px solid ${bulkResult.success ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
            borderRadius: '0.75rem',
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <span style={{ color: bulkResult.success ? '#6ee7b7' : '#fca5a5', fontSize: '0.875rem' }}>
              {bulkResult.success
                ? `✓ ${bulkResult.succeeded}/${bulkResult.total} dispositivos procesados (${bulkResult.action})`
                : `✗ Error: ${bulkResult.error}`}
            </span>
            <button onClick={() => setBulkResult(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>
              <X size={16} />
            </button>
          </div>
        )}

        {/* Statistics Cards */}
        <div className="stats-grid device-stats-grid">
          <div className="stat-card">
            <div className="stat-card-content">
              <div className="stat-header">
                <p className="stat-label" style={{color: '#60a5fa'}}>Total Dispositivos</p>
                <Cpu className="stat-icon w-10 h-10" style={{color: '#60a5fa'}} />
              </div>
              <p className="stat-value">{stats.total}</p>
              <p className="stat-description">Registrados en el sistema</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-card-content">
              <div className="stat-header">
                <p className="stat-label" style={{color: '#10b981'}}>Activos</p>
                <Activity className="stat-icon w-10 h-10" style={{color: '#10b981'}} />
              </div>
              <p className="stat-value">{stats.active}</p>
              <p className="stat-description">En línea ahora</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-card-content">
              <div className="stat-header">
                <p className="stat-label" style={{color: '#6b7280'}}>Inactivos</p>
                <Cpu className="stat-icon w-10 h-10" style={{color: '#6b7280'}} />
              </div>
              <p className="stat-value">{stats.inactive}</p>
              <p className="stat-description">Fuera de línea</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-card-content">
              <div className="stat-header">
                <p className="stat-label" style={{color: '#a78bfa'}}>Sin asignar</p>
                <Shield className="stat-icon w-10 h-10" style={{color: '#a78bfa'}} />
              </div>
              <p className="stat-value">{stats.unassigned}</p>
              <p className="stat-description">Solo fleet provisioning</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-card-content">
              <div className="stat-header">
                <p className="stat-label" style={{color: '#f59e0b'}}>ATH Activos</p>
                <Trophy className="stat-icon w-10 h-10" style={{color: '#f59e0b'}} />
              </div>
              <p className="stat-value">{ath.value}</p>
              <p className="stat-description">
                {ath.reachedAt
                  ? `Alcanzado ${new Date(ath.reachedAt).toLocaleDateString('es-AR')}`
                  : 'Sin datos aún'}
              </p>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="filters-container">
          <div className="filters-content">
            {/* Search */}
            <div className="search-wrapper">
              <Search className="search-icon w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar por nombre, MAC, modelo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            {/* Filter buttons */}
            <div className="filter-buttons">
              <button
                onClick={() => setFilter('all')}
                className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              >
                <Filter className="w-4 h-4" />
                Todos
              </button>
              <button
                onClick={() => setFilter('active')}
                className={`filter-btn ${filter === 'active' ? 'active' : ''}`}
              >
                <Activity className="w-4 h-4" />
                Activos
              </button>
              <button
                onClick={() => setFilter('inactive')}
                className={`filter-btn ${filter === 'inactive' ? 'active' : ''}`}
              >
                <Cpu className="w-4 h-4" />
                Inactivos
              </button>
              <button
                onClick={() => setFilter('unassigned')}
                className={`filter-btn ${filter === 'unassigned' ? 'active' : ''}`}
              >
                <Shield className="w-4 h-4" />
                Sin asignar ({stats.unassigned})
              </button>
            </div>
          </div>

          <div className="filter-info">
            <TrendingUp className="w-4 h-4 text-green-400" />
            <span>
              Mostrando <span className="filter-info-highlight">{filteredDevices.length}</span> de{' '}
              <span className="font-bold text-white">{allDevices.length}</span> dispositivos
            </span>
          </div>
        </div>

        {/* Devices Grid */}
        {viewMode === 'grid' && (<>
        <div className="items-grid device-items-grid">
          {paginatedDevices.map((device) => {
            const mac = device.MAC?.toUpperCase()
            const isActive = !offlineMACsSet.has(mac) && activeMACsSet.has(mac)
            const activeDevice = (activeDevices || []).find(ad => ad.mac?.toUpperCase() === mac)
            
            return (
              <div 
                key={device.$id || device.MAC} 
                className="item-card"
                onClick={() => {
                  if (bulkMode) {
                    toggleDevice(device.MAC)
                  } else {
                    navigate(`/devices/${encodeURIComponent(device.MAC)}`)
                  }
                }}
                style={{ 
                  cursor: 'pointer',
                  outline: bulkMode && selectedDevices.has(device.MAC) ? '2px solid rgba(99, 102, 241, 0.6)' : 'none',
                  outlineOffset: '-2px',
                  ...(device.isUnassigned ? { borderColor: 'rgba(167, 139, 250, 0.3)' } : {})
                }}
              >
                <div className="item-card-content">
                  {/* Header */}
                  <div className="item-header">
                    {bulkMode && (
                      <div style={{ marginRight: '0.5rem', flexShrink: 0 }}>
                        {selectedDevices.has(device.MAC)
                          ? <CheckSquare size={20} style={{ color: '#818cf8' }} />
                          : <Square size={20} style={{ color: '#4b5563' }} />
                        }
                      </div>
                    )}
                    <div style={{flex: 1, minWidth: 0}}>
                      <h3 className="item-title" style={{fontSize: '1.1rem', margin: '0 0 0.375rem 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{device.name || 'Sin nombre'}</h3>
                      <div className="item-subtitle" style={{fontSize: '0.8125rem'}}>
                        <Cpu className="w-4 h-4" />
                        <span className="font-mono text-xs">{device.MAC}</span>
                      </div>
                    </div>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '0.375rem', alignItems: 'flex-end', flexShrink: 0}}>
                      <div style={{display: 'flex', gap: '0.375rem', flexWrap: 'wrap', justifyContent: 'flex-end'}}>
                        {device.isUnassigned && (
                          <span style={{
                            fontSize: '0.65rem', fontWeight: 700,
                            padding: '0.25rem 0.5rem', borderRadius: '9999px',
                            background: 'rgba(167, 139, 250, 0.15)', color: '#c4b5fd',
                            border: '1px solid rgba(167, 139, 250, 0.3)',
                            whiteSpace: 'nowrap'
                          }}>Sin asignar</span>
                        )}
                        <div className={`status-badge ${isActive ? 'active' : 'inactive'}`}>
                          {isActive && <div className="status-dot"></div>}
                          {isActive ? 'En línea' : 'Fuera de línea'}
                        </div>
                      </div>
                      {isActive && sensorAlerts?.has(mac) && (
                        <div className="status-badge sensor-alert" title={sensorAlerts.get(mac)?.message}>
                          <AlertTriangle size={14} />
                          Sensor
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Sensor Data (only for active devices) */}
                  {isActive && activeDevice && (
                    <div style={{
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
                      border: '1px solid rgba(16, 185, 129, 0.2)',
                      borderRadius: '0.75rem',
                      padding: '0.75rem',
                      marginBottom: '1rem',
                      display: 'flex',
                      justifyContent: 'space-around',
                      alignItems: 'center',
                      gap: '1rem',
                      flexWrap: 'wrap'
                    }}>
                      {activeDevice.temperature != null && !isNaN(activeDevice.temperature) && (
                        <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                          <Thermometer className="w-4 h-4" style={{color: '#ef4444'}} />
                          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                            <p style={{fontSize: '0.65rem', color: '#94a3b8', marginBottom: '0.125rem'}}>Temp</p>
                            <p style={{fontSize: '1rem', fontWeight: '700', color: 'white'}}>
                              {parseFloat(activeDevice.temperature).toFixed(2)}°C
                            </p>
                          </div>
                        </div>
                      )}
                      {activeDevice.humidity != null && !isNaN(activeDevice.humidity) && (
                        <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                          <Droplets className="w-4 h-4" style={{color: '#3b82f6'}} />
                          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                            <p style={{fontSize: '0.65rem', color: '#94a3b8', marginBottom: '0.125rem'}}>Humedad</p>
                            <p style={{fontSize: '1rem', fontWeight: '700', color: 'white'}}>
                              {parseFloat(activeDevice.humidity).toFixed(2)}%
                            </p>
                          </div>
                        </div>
                      )}
                      {activeDevice.dpv != null && !isNaN(activeDevice.dpv) && (
                        <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                          <Activity className="w-4 h-4" style={{color: '#10b981'}} />
                          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                            <p style={{fontSize: '0.65rem', color: '#94a3b8', marginBottom: '0.125rem'}}>DPV</p>
                            <p style={{fontSize: '1rem', fontWeight: '700', color: 'white'}}>
                              {parseFloat(activeDevice.dpv).toFixed(2)}
                            </p>
                          </div>
                        </div>
                      )}
                      {activeDevice.co2 !== undefined && (
                        <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                          <Wind className="w-4 h-4" style={{color: '#8b5cf6'}} />
                          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                            <p style={{fontSize: '0.65rem', color: '#94a3b8', marginBottom: '0.125rem'}}>CO2</p>
                            <p style={{fontSize: '1rem', fontWeight: '700', color: 'white'}}>
                              {activeDevice.co2}
                            </p>
                          </div>
                        </div>
                      )}
                      {activeDevice.ph != null && (
                        <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                          <FlaskConical className="w-4 h-4" style={{color: '#22c55e'}} />
                          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                            <p style={{fontSize: '0.65rem', color: '#94a3b8', marginBottom: '0.125rem'}}>pH</p>
                            <p style={{fontSize: '1rem', fontWeight: '700', color: 'white'}}>
                              {parseFloat(activeDevice.ph).toFixed(1)}
                            </p>
                          </div>
                        </div>
                      )}
                      {activeDevice.tds != null && (
                        <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                          <Zap className="w-4 h-4" style={{color: '#f59e0b'}} />
                          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                            <p style={{fontSize: '0.65rem', color: '#94a3b8', marginBottom: '0.125rem'}}>TDS</p>
                            <p style={{fontSize: '1rem', fontWeight: '700', color: 'white'}}>
                              {Math.round(activeDevice.tds)} ppm
                            </p>
                          </div>
                        </div>
                      )}
                      {activeDevice.waterTemp != null && (
                        <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                          <Thermometer className="w-4 h-4" style={{color: '#06b6d4'}} />
                          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                            <p style={{fontSize: '0.65rem', color: '#94a3b8', marginBottom: '0.125rem'}}>Agua</p>
                            <p style={{fontSize: '1rem', fontWeight: '700', color: 'white'}}>
                              {parseFloat(activeDevice.waterTemp).toFixed(1)}°C
                            </p>
                          </div>
                        </div>
                      )}
                      {activeDevice.cameraReady != null && (
                        <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                          <Camera className="w-4 h-4" style={{color: activeDevice.cameraReady ? '#22c55e' : '#ef4444'}} />
                          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                            <p style={{fontSize: '0.65rem', color: '#94a3b8', marginBottom: '0.125rem'}}>Cámara</p>
                            <p style={{fontSize: '1rem', fontWeight: '700', color: activeDevice.cameraReady ? '#6ee7b7' : '#fca5a5'}}>
                              {activeDevice.cameraReady ? 'OK' : 'Error'}
                            </p>
                          </div>
                        </div>
                      )}
                      {activeDevice.totalCaptures != null && (
                        <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                          <Camera className="w-4 h-4" style={{color: '#a78bfa'}} />
                          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                            <p style={{fontSize: '0.65rem', color: '#94a3b8', marginBottom: '0.125rem'}}>Fotos</p>
                            <p style={{fontSize: '1rem', fontWeight: '700', color: 'white'}}>
                              {activeDevice.totalCaptures}
                            </p>
                          </div>
                        </div>
                      )}
                      {/* GP-SOIL: multi-channel moisture */}
                      {Array.isArray(activeDevice.soilChannels) && activeDevice.soilChannels.some(v => v != null) && (
                        activeDevice.soilChannels.map((v, i) => v != null && (
                          <div key={`soil-${i}`} style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                            <Droplets className="w-4 h-4" style={{color: '#b45309'}} />
                            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                              <p style={{fontSize: '0.65rem', color: '#94a3b8', marginBottom: '0.125rem'}}>
                                {activeDevice.soilChannels.filter(x => x != null).length > 1 ? `Suelo ${i + 1}` : 'Suelo'}
                              </p>
                              <p style={{fontSize: '1rem', fontWeight: '700', color: '#fcd34d'}}>
                                {parseFloat(v).toFixed(1)}%
                              </p>
                            </div>
                          </div>
                        ))
                      )}
                      {/* GP-SOIL fallback: single value when no channels array */}
                      {activeDevice.soil != null && !Array.isArray(activeDevice.soilChannels) && (
                        <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                          <Droplets className="w-4 h-4" style={{color: '#b45309'}} />
                          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                            <p style={{fontSize: '0.65rem', color: '#94a3b8', marginBottom: '0.125rem'}}>Suelo</p>
                            <p style={{fontSize: '1rem', fontWeight: '700', color: '#fcd34d'}}>
                              {parseFloat(activeDevice.soil).toFixed(1)}%
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Heartbeat / Health Data */}
                  {isActive && activeDevice?.heartbeat && (
                    <div style={{
                      background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(79, 70, 229, 0.05) 100%)',
                      border: '1px solid rgba(99, 102, 241, 0.2)',
                      borderRadius: '0.75rem',
                      padding: '0.625rem 0.75rem',
                      marginBottom: '1rem',
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr',
                      gap: '0.5rem'
                    }}>
                      {/* RSSI */}
                      {activeDevice.heartbeat.rssi !== undefined && (() => {
                        const rssi = getRssiLevel(activeDevice.heartbeat.rssi)
                        return (
                          <div style={{display: 'flex', alignItems: 'center', gap: '0.375rem'}}>
                            <Wifi className="w-3.5 h-3.5" style={{color: rssi.color}} />
                            <div>
                              <p style={{fontSize: '0.6rem', color: '#94a3b8', marginBottom: '0.05rem'}}>WiFi</p>
                              <p style={{fontSize: '0.825rem', fontWeight: '700', color: rssi.color}}>
                                {activeDevice.heartbeat.rssi} dBm
                              </p>
                            </div>
                          </div>
                        )
                      })()}

                      {/* Heap Memory */}
                      {activeDevice.heartbeat.heap !== undefined && (
                        <div style={{display: 'flex', alignItems: 'center', gap: '0.375rem'}}>
                          <HardDrive className="w-3.5 h-3.5" style={{color: getHeapColor(activeDevice.heartbeat.heap)}} />
                          <div>
                            <p style={{fontSize: '0.6rem', color: '#94a3b8', marginBottom: '0.05rem'}}>Heap</p>
                            <p style={{fontSize: '0.825rem', fontWeight: '700', color: getHeapColor(activeDevice.heartbeat.heap)}}>
                              {(activeDevice.heartbeat.heap / 1024).toFixed(0)} KB
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Uptime */}
                      {activeDevice.heartbeat.uptime !== undefined && (
                        <div style={{display: 'flex', alignItems: 'center', gap: '0.375rem'}}>
                          <Clock className="w-3.5 h-3.5" style={{color: '#a78bfa'}} />
                          <div>
                            <p style={{fontSize: '0.6rem', color: '#94a3b8', marginBottom: '0.05rem'}}>Uptime</p>
                            <p style={{fontSize: '0.825rem', fontWeight: '700', color: '#e2e8f0'}}>
                              {formatUptime(activeDevice.heartbeat.uptime)}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Min Heap (worst-case since boot) */}
                      {activeDevice.heartbeat.minHeap !== undefined && (
                        <div style={{display: 'flex', alignItems: 'center', gap: '0.375rem'}}>
                          <HardDrive className="w-3.5 h-3.5" style={{color: getHeapColor(activeDevice.heartbeat.minHeap)}} />
                          <div>
                            <p style={{fontSize: '0.6rem', color: '#94a3b8', marginBottom: '0.05rem'}}>Min Heap</p>
                            <p style={{fontSize: '0.825rem', fontWeight: '700', color: getHeapColor(activeDevice.heartbeat.minHeap)}}>
                              {(activeDevice.heartbeat.minHeap / 1024).toFixed(0)} KB
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Largest Block (contiguous free RAM) */}
                      {activeDevice.heartbeat.largestBlock !== undefined && (
                        <div style={{display: 'flex', alignItems: 'center', gap: '0.375rem'}}>
                          <HardDrive className="w-3.5 h-3.5" style={{color: getHeapColor(activeDevice.heartbeat.largestBlock)}} />
                          <div>
                            <p style={{fontSize: '0.6rem', color: '#94a3b8', marginBottom: '0.05rem'}}>Bloque Max</p>
                            <p style={{fontSize: '0.825rem', fontWeight: '700', color: getHeapColor(activeDevice.heartbeat.largestBlock)}}>
                              {(activeDevice.heartbeat.largestBlock / 1024).toFixed(0)} KB
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Heap Fragmentation */}
                      {activeDevice.heartbeat.heap_frag !== undefined && (
                        <div style={{display: 'flex', alignItems: 'center', gap: '0.375rem', gridColumn: 'span 3'}}>
                          <div style={{
                            flex: 1,
                            height: '4px',
                            background: 'rgba(255,255,255,0.1)',
                            borderRadius: '2px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${Math.min(activeDevice.heartbeat.heap_frag, 100)}%`,
                              height: '100%',
                              background: activeDevice.heartbeat.heap_frag > 50 ? '#ef4444' : activeDevice.heartbeat.heap_frag > 30 ? '#f59e0b' : '#10b981',
                              borderRadius: '2px',
                              transition: 'width 0.5s ease'
                            }} />
                          </div>
                          <span style={{fontSize: '0.65rem', color: '#94a3b8', minWidth: '60px', textAlign: 'right'}}>
                            Frag: {activeDevice.heartbeat.heap_frag}%
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Brownout Alert Badge */}
                  {activeDevice?.brownoutCount > 0 && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '0.375rem',
                      padding: '0.3rem 0.6rem', borderRadius: '6px',
                      background: activeDevice.brownoutCount > 2 ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.15)',
                      border: `1px solid ${activeDevice.brownoutCount > 2 ? 'rgba(239,68,68,0.3)' : 'rgba(245,158,11,0.3)'}`,
                    }}>
                      <Zap size={12} style={{ color: activeDevice.brownoutCount > 2 ? '#ef4444' : '#f59e0b' }} />
                      <span style={{
                        fontSize: '0.7rem', fontWeight: 600,
                        color: activeDevice.brownoutCount > 2 ? '#ef4444' : '#f59e0b'
                      }}>
                        {activeDevice.brownoutCount} brownout{activeDevice.brownoutCount !== 1 ? 's' : ''} (24h)
                      </span>
                      {activeDevice.txPower != null && activeDevice.txPower < 17 && (
                        <span style={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                          · TX {activeDevice.txPower} dBm
                        </span>
                      )}
                    </div>
                  )}

                  {/* Info Grid */}
                  <div className="item-info-grid">
                    <div className="info-row">
                      <span className="info-label">Modelo</span>
                      <span className="info-value">{device.model || 'N/A'}</span>
                    </div>

                    {activeDevice?.sensorName && (
                      <div className="info-row">
                        <span className="info-label">Sensor</span>
                        <span className="info-value">{activeDevice.sensorName}</span>
                      </div>
                    )}

                    {device.authUser && (
                      <div className="info-row">
                        <span className="info-label">
                          <User className="w-4 h-4 text-blue-400" />
                          Cliente
                        </span>
                        <span className="info-value">{device.authUser.name}</span>
                      </div>
                    )}

                    {device.growRoom && (
                      <div className="info-row">
                        <span className="info-label">Grow Room</span>
                        <span className="info-value">{device.growRoom.name}</span>
                      </div>
                    )}

                    {device.$createdAt && (
                      <div className="info-row">
                        <span className="info-label">
                          <Calendar className="w-4 h-4 text-purple-400" />
                          Registrado
                        </span>
                        <span className="info-value">{formatDate(device.$createdAt)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Empty State */}
        {filteredDevices.length === 0 && !loading && (
          <div className="empty-state">
            <div className="empty-state-icon">
              <Cpu className="w-16 h-16 text-gray-600" />
            </div>
            <p className="empty-state-title">
              {searchTerm || filter !== 'all' 
                ? 'No se encontraron dispositivos con los filtros aplicados'
                : 'No hay dispositivos registrados'}
            </p>
            {searchTerm && <p className="empty-state-description">Intenta con otros términos de búsqueda</p>}
          </div>
        )}

        {/* Pagination */}
        {filteredDevices.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filteredDevices.length}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setCurrentPage}
          />
        )}
        </>)}

        {/* Device Map / Topology View */}
        {viewMode === 'map' && (
          <DeviceTopologyMap
            devicesByClient={devicesByClient}
            activeDevices={activeDevices}
            activeMACsSet={activeMACsSet}
            offlineMACsSet={offlineMACsSet}
            navigate={navigate}
            sensorAlerts={sensorAlerts}
          />
        )}

        {/* Fleet Provisioning View */}
        {viewMode === 'fleet' && (
          <div className="fleet-section">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
              <h2 style={{ fontSize: '1.15rem', color: '#e2e8f0', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Shield size={20} style={{ color: '#a78bfa' }} /> Fleet Provisioning — IoT Things ({fleetThings.length})
              </h2>
              <div style={{ position: 'relative' }}>
                <Search size={14} style={{ position: 'absolute', left: '0.5rem', top: '50%', transform: 'translateY(-50%)', color: '#64748b' }} />
                <input
                  type="text" placeholder="Buscar thing..."
                  value={fleetSearch} onChange={e => setFleetSearch(e.target.value)}
                  style={{ background: 'rgba(15,23,42,0.7)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: '0.5rem', color: '#e2e8f0', padding: '0.4rem 0.5rem 0.4rem 1.75rem', fontSize: '0.8rem', width: '200px' }}
                />
              </div>
            </div>

            {fleetLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>Cargando IoT Things...</div>
            ) : (() => {
              const filtered = fleetThings.filter(t => !fleetSearch || t.thingName.toLowerCase().includes(fleetSearch.toLowerCase()))
              // Cross-reference with registered devices
              const registeredMACs = new Set(allDevices.map(d => (d.MAC || d.mac || '').toUpperCase()))
              const registeredThingNames = new Set(allDevices.map(d => {
                const mac = (d.MAC || d.mac || '').toUpperCase()
                return 'GP_' + mac.replace(/:/g, '_')
              }))

              const provisioned = filtered.filter(t => registeredThingNames.has(t.thingName) || registeredMACs.has(t.thingName))
              const pending = filtered.filter(t => !registeredThingNames.has(t.thingName) && !registeredMACs.has(t.thingName))

              return (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
                    {[
                      { label: 'Total Things', value: fleetThings.length, color: '#a78bfa' },
                      { label: 'Provisionados', value: provisioned.length, color: '#4ade80' },
                      { label: 'Pendientes', value: pending.length, color: '#f59e0b' },
                      { label: 'Registrados App', value: registeredMACs.size, color: '#3b82f6' },
                    ].map(s => (
                      <div key={s.label} style={{ background: 'rgba(30,41,59,0.6)', borderRadius: '0.75rem', padding: '1rem', border: '1px solid rgba(51,65,85,0.5)', textAlign: 'center' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: 800, color: s.color }}>{s.value}</div>
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{s.label}</div>
                      </div>
                    ))}
                  </div>

                  {pending.length > 0 && (
                    <div style={{ marginBottom: '1.5rem' }}>
                      <h3 style={{ fontSize: '0.9rem', color: '#f59e0b', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                        <Clock size={14} /> Pendientes de registro ({pending.length})
                      </h3>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '0.5rem' }}>
                        {pending.map(t => (
                          <div key={t.thingName} style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '0.5rem', padding: '0.75rem' }}>
                            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#fbbf24', fontFamily: 'monospace' }}>{t.thingName}</div>
                            {t.thingTypeName && <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '0.25rem' }}>Tipo: {t.thingTypeName}</div>}
                            {t.attributes && Object.keys(t.attributes).length > 0 && (
                              <div style={{ display: 'flex', gap: '0.25rem', marginTop: '0.375rem', flexWrap: 'wrap' }}>
                                {Object.entries(t.attributes).map(([k, v]) => (
                                  <span key={k} style={{ fontSize: '0.65rem', padding: '0.125rem 0.375rem', borderRadius: '4px', background: 'rgba(251,191,36,0.15)', color: '#fcd34d' }}>{k}: {v}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <h3 style={{ fontSize: '0.9rem', color: '#4ade80', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                    <Shield size={14} /> Provisionados ({provisioned.length})
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '0.5rem' }}>
                    {provisioned.map(t => {
                      const isActive = activeMACsSet.has(t.attributes?.mac?.toUpperCase())
                      return (
                        <div key={t.thingName} style={{ background: isActive ? 'rgba(16,185,129,0.08)' : 'rgba(30,41,59,0.5)', border: `1px solid ${isActive ? 'rgba(16,185,129,0.3)' : 'rgba(51,65,85,0.5)'}`, borderRadius: '0.5rem', padding: '0.75rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: isActive ? '#4ade80' : '#6b7280' }} />
                            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#e2e8f0', fontFamily: 'monospace' }}>{t.thingName}</span>
                          </div>
                          {t.thingTypeName && <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '0.25rem' }}>Tipo: {t.thingTypeName}</div>}
                          {t.attributes && Object.keys(t.attributes).length > 0 && (
                            <div style={{ display: 'flex', gap: '0.25rem', marginTop: '0.375rem', flexWrap: 'wrap' }}>
                              {Object.entries(t.attributes).map(([k, v]) => (
                                <span key={k} style={{ fontSize: '0.65rem', padding: '0.125rem 0.375rem', borderRadius: '4px', background: 'rgba(16,185,129,0.15)', color: '#6ee7b7' }}>{k}: {v}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    })}
                    {provisioned.length === 0 && (
                      <div style={{ padding: '1rem', color: '#64748b', textAlign: 'center', gridColumn: '1 / -1' }}>No hay things provisionados</div>
                    )}
                  </div>
                </>
              )
            })()}
          </div>
        )}
      </div>

      {/* Device Comparator Overlay */}
      {compareMode && selectedDevices.size >= 2 && (
        <div className="modal-overlay" onClick={() => setCompareMode(false)} style={{ zIndex: 1000 }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#0f172a', borderRadius: '1rem', padding: '1.5rem',
            maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto',
            border: '1px solid rgba(99, 102, 241, 0.3)',
            width: `${Math.min(selectedDevices.size * 280 + 100, 1200)}px`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ fontSize: '1.25rem', color: '#fff', fontWeight: 700 }}>
                <GitCompare size={18} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.5rem' }} />
                Comparador de dispositivos
              </h2>
              <button onClick={() => setCompareMode(false)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${selectedDevices.size}, 1fr)`,
              gap: '1rem'
            }}>
              {Array.from(selectedDevices).map(mac => {
                const dev = allDevices.find(d => d.MAC === mac)
                const activeDevice = (activeDevices || []).find(ad => ad.mac?.toUpperCase() === mac?.toUpperCase())
                const isActive = !offlineMACsSet.has(mac?.toUpperCase()) && activeMACsSet.has(mac?.toUpperCase())
                if (!dev) return null

                return (
                  <div key={mac} style={{
                    background: 'rgba(30, 41, 59, 0.6)',
                    borderRadius: '0.75rem',
                    padding: '1rem',
                    border: '1px solid rgba(51,65,85,0.5)'
                  }}>
                    {/* Name + Status */}
                    <div style={{ marginBottom: '0.75rem' }}>
                      <h3 style={{ fontSize: '1rem', color: '#fff', fontWeight: 700, marginBottom: '0.25rem' }}>
                        {dev.name || 'Sin nombre'}
                      </h3>
                      <div style={{ fontSize: '0.7rem', color: '#94a3b8', fontFamily: 'monospace' }}>{mac}</div>
                      <div style={{
                        display: 'inline-block', marginTop: '0.25rem',
                        padding: '0.125rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600,
                        background: isActive ? 'rgba(16,185,129,0.2)' : 'rgba(100,100,100,0.2)',
                        color: isActive ? '#6ee7b7' : '#6b7280'
                      }}>{isActive ? '● Online' : '○ Offline'}</div>
                    </div>

                    {/* Metrics table */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {[
                        { label: 'Temp', value: activeDevice?.temperature, unit: '°C', icon: <Thermometer size={14} style={{ color: '#ef4444' }} /> },
                        { label: 'Hum', value: activeDevice?.humidity, unit: '%', icon: <Droplets size={14} style={{ color: '#3b82f6' }} /> },
                        { label: 'DPV', value: activeDevice?.dpv, unit: ' kPa', icon: <Activity size={14} style={{ color: '#10b981' }} /> },
                        { label: 'CO₂', value: activeDevice?.co2, unit: ' ppm', icon: <Wind size={14} style={{ color: '#8b5cf6' }} /> },
                        { label: 'pH', value: activeDevice?.ph, unit: '', icon: <FlaskConical size={14} style={{ color: '#22c55e' }} /> },
                        { label: 'TDS', value: activeDevice?.tds, unit: ' ppm', icon: <Zap size={14} style={{ color: '#f59e0b' }} /> },
                        { label: 'Agua', value: activeDevice?.waterTemp, unit: '°C', icon: <Thermometer size={14} style={{ color: '#06b6d4' }} /> },
                        { label: 'Fotos', value: activeDevice?.totalCaptures, unit: '', icon: <Camera size={14} style={{ color: '#a78bfa' }} /> },
                        { label: 'Suelo', value: activeDevice?.soil ?? (Array.isArray(activeDevice?.soilChannels) ? activeDevice.soilChannels.find(v => v != null) : undefined), unit: '%', icon: <Droplets size={14} style={{ color: '#b45309' }} /> },
                        { label: 'WiFi', value: activeDevice?.heartbeat?.rssi ?? activeDevice?.rssi, unit: ' dBm', icon: <Wifi size={14} style={{ color: '#f59e0b' }} /> },
                        { label: 'Heap', value: activeDevice?.heartbeat?.heap, unit: ' B', icon: <HardDrive size={14} style={{ color: '#06b6d4' }} />, fmt: v => `${(v / 1024).toFixed(0)} KB` },
                        { label: 'Uptime', value: activeDevice?.heartbeat?.uptime, unit: '', icon: <Clock size={14} style={{ color: '#a78bfa' }} />, fmt: v => { const d = Math.floor(v / 86400); const h = Math.floor((v % 86400) / 3600); return d > 0 ? `${d}d ${h}h` : `${h}h` } },
                      ].map(({ label, value, unit, icon, fmt }) => (
                        <div key={label} style={{
                          display: 'flex', alignItems: 'center', gap: '0.375rem',
                          padding: '0.375rem 0.5rem',
                          background: 'rgba(0,0,0,0.2)',
                          borderRadius: '0.375rem'
                        }}>
                          {icon}
                          <span style={{ fontSize: '0.75rem', color: '#94a3b8', flex: 1 }}>{label}</span>
                          <span style={{ fontSize: '0.85rem', fontWeight: 700, color: value != null ? '#fff' : '#4b5563' }}>
                            {value != null ? (fmt ? fmt(value) : `${Number(value).toFixed(1)}${unit}`) : '—'}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Switches summary */}
                    {activeDevice?.switchStates && (
                      <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
                        {Object.entries(activeDevice.switchStates).map(([name, state]) => (
                          <span key={name} style={{
                            fontSize: '0.65rem', padding: '0.125rem 0.375rem', borderRadius: '4px',
                            background: state ? 'rgba(16,185,129,0.2)' : 'rgba(100,100,100,0.2)',
                            color: state ? '#6ee7b7' : '#6b7280'
                          }}>{name}: {state ? 'ON' : 'OFF'}</span>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* Bulk Config Modal */}
      {configModalOpen && (
        <div
          className="modal-overlay"
          onClick={() => { setConfigModalOpen(false); setBulkConfigInterval('') }}
          style={{ zIndex: 1000 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#1e1b4b',
              border: '1px solid rgba(99,102,241,0.35)',
              borderRadius: '0.875rem',
              width: '100%',
              maxWidth: 380,
              boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
              color: '#e2e8f0',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.25rem', borderBottom: '1px solid rgba(99,102,241,0.2)' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#a5b4fc', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Settings size={16} /> Configurar {selectedDevices.size} dispositivo{selectedDevices.size !== 1 ? 's' : ''}
              </h3>
              <button onClick={() => { setConfigModalOpen(false); setBulkConfigInterval('') }} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>
            <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                <label style={{ fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Intervalo de telemetría (segundos)
                </label>
                <input
                  type="number"
                  min={1}
                  max={3600}
                  value={bulkConfigInterval}
                  onChange={e => setBulkConfigInterval(e.target.value)}
                  placeholder="ej: 30"
                  style={{
                    background: 'rgba(15,23,42,0.7)',
                    border: '1px solid rgba(99,102,241,0.3)',
                    borderRadius: '0.45rem',
                    color: '#e2e8f0',
                    padding: '0.5rem 0.75rem',
                    fontSize: '0.9rem',
                    outline: 'none',
                  }}
                />
                <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
                  Rango válido: 1–3600 segundos. El dispositivo debe estar online.
                </small>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', padding: '1rem 1.25rem', borderTop: '1px solid rgba(99,102,241,0.2)' }}>
              <button
                onClick={() => { setConfigModalOpen(false); setBulkConfigInterval('') }}
                style={{ background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.25)', color: '#94a3b8', padding: '0.5rem 1rem', borderRadius: '0.45rem', cursor: 'pointer', fontSize: '0.875rem' }}
              >
                Cancelar
              </button>
              <button
                onClick={handleBulkConfig}
                disabled={!bulkConfigInterval || bulkConfigLoading}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                  background: 'rgba(251,191,36,0.2)',
                  border: '1px solid rgba(251,191,36,0.4)',
                  color: '#fde68a',
                  padding: '0.5rem 1.15rem',
                  borderRadius: '0.45rem',
                  cursor: bulkConfigInterval && !bulkConfigLoading ? 'pointer' : 'not-allowed',
                  fontSize: '0.875rem', fontWeight: 600,
                  opacity: !bulkConfigInterval || bulkConfigLoading ? 0.5 : 1,
                }}
              >
                <Settings size={14} /> {bulkConfigLoading ? 'Enviando…' : 'Aplicar configuración'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

// ── Device Topology Map Component ──
function DeviceTopologyMap({ devicesByClient, activeDevices, activeMACsSet, offlineMACsSet, navigate, sensorAlerts }) {
  const getDeviceStatus = (dev) => {
    const mac = (dev.MAC || dev.mac || '').toUpperCase()
    return !offlineMACsSet.has(mac) && (dev.isOnline || activeMACsSet.has(mac))
  }

  const getLiveData = (dev) => {
    const mac = (dev.MAC || dev.mac || '').toUpperCase()
    return dev.liveData || (activeDevices || []).find(d => d.mac?.toUpperCase() === mac)
  }

  return (
    <div className="topo-container">
      {devicesByClient.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><Network className="w-16 h-16 text-gray-600" /></div>
          <p className="empty-state-title">No hay dispositivos registrados</p>
        </div>
      ) : (
        devicesByClient.map(([clientId, group]) => {
          const onlineCount = group.devices.filter(d => getDeviceStatus(d)).length
          return (
            <div key={clientId} className="topo-client">
              {/* Client header */}
              <div className="topo-client-header">
                <div className="topo-client-info">
                  <div className="topo-client-avatar">
                    <User size={18} />
                  </div>
                  <div>
                    <div className="topo-client-name">{group.name}</div>
                    {group.email && <div className="topo-client-email">{group.email}</div>}
                  </div>
                </div>
                <span className={`topo-online-badge ${onlineCount > 0 ? 'has-online' : 'all-offline'}`}>
                  {onlineCount}/{group.devices.length} online
                </span>
              </div>

              {/* Devices grid inside client */}
              <div className="topo-devices-grid">
                {group.devices.map(dev => {
                  const isOnline = getDeviceStatus(dev)
                  const live = getLiveData(dev)
                  const mac = dev.MAC || dev.mac || ''

                  return (
                    <div
                      key={dev.$id}
                      onClick={() => navigate(`/devices/${encodeURIComponent(mac)}`)}
                      className={`topo-device ${isOnline ? 'online' : 'offline'}`}
                    >
                      <div className={`topo-status-dot ${isOnline ? 'online' : 'offline'}`} />

                      <div style={{ marginBottom: '0.5rem' }}>
                        <div className="topo-device-name">{dev.name || 'Sin nombre'}</div>
                        <div className="topo-device-mac">{mac}</div>
                        {isOnline && sensorAlerts?.has(mac.toUpperCase()) && (
                          <div className="topo-tag sensor-alert-tag" title={sensorAlerts.get(mac.toUpperCase())?.message}>
                            <AlertTriangle size={10} /> Sensor
                          </div>
                        )}
                      </div>

                      <div className="topo-tags">
                        {dev.growRoom && <span className="topo-tag room">{dev.growRoom.name}</span>}
                        {dev.model && <span className="topo-tag model">{dev.model}</span>}
                        {dev.firmwareVersion && <span className="topo-tag firmware">v{dev.firmwareVersion}</span>}
                      </div>

                      {isOnline && live && (
                        <div className="topo-sensors">
                          {live.temperature !== undefined && (
                            <div className="topo-sensor">
                              <Thermometer size={12} style={{ color: '#ef4444' }} />
                              <span className="topo-sensor-value">{Number(live.temperature).toFixed(1)}°</span>
                            </div>
                          )}
                          {live.humidity !== undefined && (
                            <div className="topo-sensor">
                              <Droplets size={12} style={{ color: '#3b82f6' }} />
                              <span className="topo-sensor-value">{Number(live.humidity).toFixed(1)}%</span>
                            </div>
                          )}
                          {live.dpv !== undefined && (
                            <div className="topo-sensor">
                              <Activity size={12} style={{ color: '#10b981' }} />
                              <span className="topo-sensor-value">{Number(live.dpv).toFixed(2)}</span>
                            </div>
                          )}
                          {(live.heartbeat?.rssi ?? live.rssi) !== undefined && (
                            <div className="topo-sensor">
                              <Wifi size={12} style={{ color: '#f59e0b' }} />
                              <span className="topo-sensor-value">{live.heartbeat?.rssi ?? live.rssi} dBm</span>
                            </div>
                          )}
                          {live.ph != null && (
                            <div className="topo-sensor">
                              <FlaskConical size={12} style={{ color: '#22c55e' }} />
                              <span className="topo-sensor-value">{Number(live.ph).toFixed(1)} pH</span>
                            </div>
                          )}
                          {live.tds != null && (
                            <div className="topo-sensor">
                              <Zap size={12} style={{ color: '#f59e0b' }} />
                              <span className="topo-sensor-value">{Math.round(live.tds)} ppm</span>
                            </div>
                          )}
                          {live.waterTemp != null && (
                            <div className="topo-sensor">
                              <Thermometer size={12} style={{ color: '#06b6d4' }} />
                              <span className="topo-sensor-value">{Number(live.waterTemp).toFixed(1)}°</span>
                            </div>
                          )}
                          {live.cameraReady != null && (
                            <div className="topo-sensor">
                              <Camera size={12} style={{ color: live.cameraReady ? '#22c55e' : '#ef4444' }} />
                              <span className="topo-sensor-value">{live.cameraReady ? 'OK' : 'Err'}</span>
                            </div>
                          )}
                          {(live.soil != null || (Array.isArray(live.soilChannels) && live.soilChannels.some(v => v != null))) && (
                            <div className="topo-sensor">
                              <Droplets size={12} style={{ color: '#b45309' }} />
                              <span className="topo-sensor-value" style={{ color: '#fcd34d' }}>
                                {parseFloat(Array.isArray(live.soilChannels) ? live.soilChannels.find(v => v != null) : live.soil).toFixed(1)}%
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      {isOnline && live?.switchStates && (
                        <div className="topo-switches">
                          {Object.entries(live.switchStates).map(([name, state]) => (
                            <span key={name} className={`topo-switch ${state ? 'on' : 'off'}`}>
                              {name}: {state ? 'ON' : 'OFF'}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })
      )}
    </div>
  )
}
